import { useCallback, useEffect, useState } from "react";
import { useBlocker } from "react-router";
import { useCanBlock } from "ra-core";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useSaveMeeting } from "./useSaveMeeting";

/** "the summary", "the tasks", "the summary and the tasks". */
const joinLabels = (labels: string[]) =>
  labels.length <= 1
    ? (labels[0] ?? "")
    : `${labels.slice(0, -1).join(", ")} and ${labels[labels.length - 1]}`;

/**
 * Warns before leaving the meeting with unsaved work, naming what is unsaved.
 *
 * Two constraints, both of which would otherwise ship this broken:
 *
 * - **Pathname only.** Switching between the recording, transcript and summary
 *   is a router navigation on the same path (MeetingAssetBar uses
 *   setSearchParams), so a guard that fired on any navigation would prompt on
 *   every asset switch.
 * - **Reload and tab close get `beforeunload` and nothing more.** The browser
 *   will not show our buttons there, and gives us no chance to save.
 *
 * Known quirk, accepted rather than fixed: deleting the meeting navigates to the
 * list, so with unsaved work the prompt offers to save a record that is about to
 * be deleted. Suppressing it would mean hooking the delete confirmation and
 * un-suppressing when it is cancelled, which is more moving parts than the
 * annoyance is worth. Deleting a meeting is rare, and Discard is right there.
 */
const NavigationPrompt = () => {
  const { dirtyLabels, saveDirty } = useSaveMeeting();
  const [saving, setSaving] = useState(false);
  const hasUnsaved = dirtyLabels.length > 0;

  const blocker = useBlocker(
    useCallback(
      ({
        currentLocation,
        nextLocation,
      }: {
        currentLocation: { pathname: string };
        nextLocation: { pathname: string };
      }) => hasUnsaved && nextLocation.pathname !== currentLocation.pathname,
      [hasUnsaved],
    ),
  );

  if (blocker.state !== "blocked") return null;

  return (
    <Dialog open onOpenChange={() => blocker.reset?.()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Save your changes?</DialogTitle>
          <DialogDescription>
            You have unsaved changes to {joinLabels(dirtyLabels)}.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            onClick={() => blocker.reset?.()}
          >
            Stay on this page
          </Button>
          <Button
            type="button"
            variant="outline"
            // Clearing the dirty flags does not release a blocker that has
            // already fired, so proceed explicitly.
            onClick={() => blocker.proceed?.()}
          >
            Discard
          </Button>
          <Button
            type="button"
            disabled={saving}
            onClick={async () => {
              setSaving(true);
              const ok = await saveDirty();
              setSaving(false);
              // A failed save keeps the prompt open — leaving now would lose the
              // work the save just failed to store.
              if (ok) blocker.proceed?.();
            }}
          >
            {saving ? "Saving..." : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const UnloadPrompt = () => {
  const { dirtyLabels } = useSaveMeeting();
  const hasUnsaved = dirtyLabels.length > 0;

  useEffect(() => {
    if (!hasUnsaved) return;
    const handler = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      // Required by older browsers to trigger the warning at all.
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [hasUnsaved]);

  return null;
};

export const LeaveGuard = () => {
  // useBlocker throws outright without a data router, and the browser test
  // harness mounts a plain MemoryRouter. Gated, those contexts fall back to the
  // unload warning instead of crashing.
  const canBlock = useCanBlock();
  return (
    <>
      {canBlock ? <NavigationPrompt /> : null}
      <UnloadPrompt />
    </>
  );
};
